/**
 * DebugPanel Component
 *
 * Floating debug panel for visualizing animation state during playback.
 * Displays current time, body part rotations, and active tool information.
 *
 * Features:
 * - Current playhead time formatted as "X.Xs"
 * - All 6 body parts with current rotation values
 * - Delta and target rotation when a tool is active
 * - Semi-transparent dark background for overlay use
 * - Monospace font for alignment of rotation values
 */

import type { AnimationState } from './hooks/useAnimationUpdate';
import type { Tool } from '../../stores/timelineStore';
import { useTimelineStore } from '../../stores/timelineStore';
import type { MotorBodyPart, MotorPositions } from '../../stores/puppetStore';
import { usePuppetStore } from '../../stores/puppetStore';

/**
 * Props for the DebugPanel component
 */
export interface DebugPanelProps {
  /** Whether the panel is visible */
  visible: boolean;
  /** Current animation state from useAnimationUpdate hook */
  animationState: AnimationState | null;
}

/**
 * All motor body parts in display order
 */
const BODY_PARTS: MotorBodyPart[] = [
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
];

/**
 * Display labels for body parts
 */
const BODY_PART_LABELS: Record<MotorBodyPart, string> = {
  head: 'Head',
  body: 'Body',
  leftArm: 'Left Arm',
  rightArm: 'Right Arm',
  leftLeg: 'Left Leg',
  rightLeg: 'Right Leg',
};

/**
 * Default motor positions when animation state is null
 */
const DEFAULT_POSITIONS: MotorPositions = {
  head: 0,
  body: 0,
  leftArm: 0,
  rightArm: 0,
  leftLeg: 0,
  rightLeg: 0,
};

/**
 * Find the winning tool for a body part at the current playhead position.
 *
 * Uses "last-wins" logic: when multiple tools affect the same body part,
 * the tool with the later startTime takes full control.
 *
 * @param tools - Array of all tools on the timeline
 * @param bodyPart - The body part to find active tool for
 * @param playheadPosition - Current playhead position in seconds
 * @returns The winning tool, or null if no active tool
 */
function findWinningToolForBodyPart(
  tools: Tool[],
  bodyPart: MotorBodyPart,
  playheadPosition: number
): Tool | null {
  // Filter tools by body part and active time range
  const activeTools = tools.filter((tool) => {
    // Skip control tools
    if (tool.bodyPart === 'control') return false;
    // Check body part matches
    if (tool.bodyPart !== bodyPart) return false;
    // Check if playhead is within tool time range
    const endTime = tool.startTime + tool.duration;
    return playheadPosition >= tool.startTime && playheadPosition <= endTime;
  });

  if (activeTools.length === 0) return null;
  if (activeTools.length === 1) return activeTools[0];

  // Last-wins: sort by startTime descending, later start wins
  return [...activeTools].sort((a, b) => b.startTime - a.startTime)[0];
}

/**
 * Format rotation value with sign prefix for delta display
 *
 * @param delta - Delta rotation in degrees
 * @returns Formatted string like "+15" or "-30"
 */
function formatDelta(delta: number): string {
  const rounded = Math.round(delta);
  return delta >= 0 ? `+${rounded}` : `${rounded}`;
}

/**
 * DebugPanel Component
 *
 * Displays animation debug information in a floating overlay panel.
 */
export function DebugPanel({
  visible,
  animationState,
}: DebugPanelProps): JSX.Element | null {
  // Subscribe to timeline store for playhead and tools
  // NOTE: These subscriptions are intentional - DebugPanel is a real-time debug tool
  // that needs to update frequently. The key optimization is that Canvas3DPreview
  // no longer subscribes to these, so the 3D canvas doesn't re-render 60x/second.
  const playheadPosition = useTimelineStore((state) => state.playheadPosition);
  const tools = useTimelineStore((state) => state.tools);

  // Get starting positions from puppet config (may be undefined in tests)
  const startingPosition =
    usePuppetStore((state) => state.startingPosition) ?? DEFAULT_POSITIONS;

  // Don't render if not visible
  if (!visible) return null;

  // Get current rotations from animation state or use defaults
  const currentRotations =
    animationState?.currentRotations ?? DEFAULT_POSITIONS;

  // Format playhead time to 1 decimal place
  const formattedTime = playheadPosition.toFixed(1);

  return (
    <div
      data-testid="debug-panel"
      className="w-[280px] rounded-lg bg-black/80 p-3 font-mono text-xs text-white shadow-lg"
    >
      {/* Header with current time */}
      <div className="mb-3 border-b border-white/20 pb-2 text-sm font-semibold">
        Time: {formattedTime}s
      </div>

      {/* Body part rotation list */}
      <div className="space-y-1">
        {BODY_PARTS.map((bodyPart) => {
          const currentRotation = Math.round(currentRotations[bodyPart]);
          const winningTool = findWinningToolForBodyPart(
            tools,
            bodyPart,
            playheadPosition
          );

          // Format the display string
          // Format: "from° (current°) → ±remaining° (target°)" when active tool
          // Format: "current° (idle)" when no active tool
          let displayString: string;
          if (winningTool) {
            const targetRotation = Math.round(winningTool.targetRotation);
            // Get the "from" rotation from animation state (captured when tool started)
            const fromRotation = Math.round(
              animationState?.activeToolFromRotations?.[bodyPart] ??
                startingPosition[bodyPart] ??
                0
            );
            const remaining = targetRotation - currentRotation;
            // X° (Y°) → ±A° (B°)
            // X = from, Y = current, A = remaining, B = target
            displayString = `${fromRotation}\u00B0 (${currentRotation}\u00B0) \u2192 ${formatDelta(remaining)}\u00B0 (${targetRotation}\u00B0)`;
          } else {
            displayString = `${currentRotation}\u00B0 (idle)`;
          }

          return (
            <div key={bodyPart} className="flex justify-between">
              <span className="text-gray-400">
                {BODY_PART_LABELS[bodyPart]}:
              </span>
              <span
                className={winningTool ? 'text-green-400' : 'text-gray-300'}
              >
                {displayString}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
